/* jshint esversion: 6 */
/**
 * IbD3ScatterChart class for creating and managing a scatter chart using D3.js.
 */
class IbD3ScatterChart {
    /**
     * Constructor for IbD3ScatterChart.
     * @param {Object} $window - The window object.
     * @param {Object} $timeout - The timeout service.
     * @param {Object} $translate - The translation service.
     */
    constructor($window, $timeout, $translate) {
        this.$window = $window;
        this.$timeout = $timeout;
        this.$translate = $translate;

        this.line = null;
        this.d3chart = null;
        this.chartContainer = null;
        this.minX = null;
        this.maxX = null;
        this.minY = null;
        this.maxY = null;
        this.width = null;
        this.height = null;
        this.paddingLeft = 75;
        this.paddingTop = 32;
        this.paddingBottom = 50;
        this.paddingRight = 20;
        this.pixelToCoordX = null;
        this.pixelToCoordY = null;
        this.coordToPixelX = null;
        this.coordToPixelY = null;
        this.mouseArray = [0, 0];
        this.tooltipDelay = 0;
        this.drawLine = false;
        this.moveLine = false;
        this.showText = false;
        this.showLine = false;
        this.lineColor = "black";
        this.lineStyle = "8,8";
        this.dotColor = "gray";
        this.lineM = 0;
        this.lineB = 0;
        this.oldCanAlign1 = false;
        this.oldCanAlign2 = false;
        this.oldCanMove = false;
        this.dot1 = null;
        this.dot2 = null;
        this.canShowAutoLine = false;
        this.canDrawLine = false;
        this.moveOffset = 10;
        this.alignOffset = 10;
        this.lineButton = null;
        this.isPDF = false;
        this.icons = {
            'draw': '/images/lobf-0.png',
            'show': '/images/lobf-1.png',
            'hide': '/images/lobf-2.png'
        };
        this.offsetLeft = 0;
        this.offsetTop = 0;
        this.canvasEdge = -15;
    }

    /**
     * Link function to bind scope and element to the chart.
     * @param {Object} scope - The scope object.
     * @param {Object} element - The element object.
     */
    link(scope, element) {
        scope.$watch('chartData', () => {
            this.showLine = false;
            this.init(scope, element);
        });

        scope.$watch('$parent.contentObject.getPreferences().canShowAutoLine', () => {
            if (this.d3chart) {
                this.canShowAutoLine = scope.$parent.contentObject.getPreferences().canShowAutoLine;
                this.canDrawLine = false;
                this.showLine = false;
                this.$window.d3.selectAll("svg > line").remove();
                if (this.canShowAutoLine) {
                    scope.line = {x1: 0, x2: 0, y1: 0, y2: 0, show: scope.line.show};
                    this.line = this.generateAutoLine(scope);
                    this.chartContainer.off("mousedown");
                } else {
                    this.line = null;
                    scope.line = {x1: 0, x2: 0, y1: 0, y2: 0, show: scope.line.show};
                    this.init(scope, element);
                }
                this.displayButton(scope);
                this.chartContainer.css("cursor", "");
            }
        });

        scope.$watch('$parent.contentObject.getPreferences().canDrawLine', () => {
            if (this.d3chart) {
                this.canDrawLine = scope.$parent.contentObject.getPreferences().canDrawLine && !this.canShowAutoLine;
                this.showLine = false;
                this.$window.d3.selectAll("svg > line").remove();
                if (this.canDrawLine) {
                    this.chartContainer.on('mousedown', this.mousedown.bind(this, scope));
                } else {
                    this.chartContainer.off('mousedown');
                    this.line = this.canShowAutoLine ? this.generateAutoLine(scope) : null;
                    scope.line = {x1: 0, x2: 0, y1: 0, y2: 0, show: scope.line.show};
                }
                this.displayButton(scope);
                this.chartContainer.css("cursor", "");
                this.init(scope, element);
            }
        });

        scope.$watch('line', () => {
            this.init(scope, element);
        });

        scope.$watch('yLabel', () => {
            this.init(scope, element);
        });
    }

    /**
     * Get the value at the given mouse coordinates.
     * @param {number} mouseX - The x coordinate of the mouse.
     * @param {number} mouseY - The y coordinate of the mouse.
     * @returns {Array} - The value at the given coordinates.
     */
    getValue(mouseX, mouseY) {
        return [this.pixelToCoordX(mouseX), this.pixelToCoordY(mouseY)];
    }

    /**
     * Handle mousedown event.
     * @param {Object} scope - The scope object.
     * @param {Object} e - The event object.
     */
    mousedown(scope, e) {
        var parentOffset = $(this.chartContainer).offset();
        var m = [e.pageX - parentOffset.left, e.pageY - parentOffset.top];

        if (!this.showLine || !this.insideGraph(m, true)) {
            return;
        }
        this.d3chart.selectAll("svg > line").remove();
        this.drawLine = true;

        this.line = this.d3chart.append("line")
            .attr("x1", m[0])
            .attr("y1", m[1])
            .attr("x2", m[0])
            .attr("y2", m[1])
            .attr("stroke-dasharray", this.lineStyle)
            .style("stroke", this.lineColor)
            .attr("stroke-width", 3)
            .attr("class", "lobf auto");
        this.chartContainer.off('mousedown');
        this.chartContainer.on("mousedown", this.secondMouseDown.bind(this, scope));
    }

    /**
     * Check if the given coordinates are inside the graph.
     * @param {Array} m - The coordinates to check.
     * @param {boolean} useEdge - Whether to use the edge.
     * @returns {boolean} - True if inside the graph, false otherwise.
     */
    insideGraph(m, useEdge) {
        var edge = useEdge ? this.canvasEdge : 0;
        return (
            m[0] >= this.paddingLeft + edge &&
            m[0] <= this.width - edge - this.paddingRight &&
            m[1] >= this.paddingTop + edge &&
            m[1] <= this.height - edge - this.paddingBottom
        );
    }

    /**
     * Handle mousemove event.
     * @param {Object} e - The event object.
     */
    mousemove(e) {
        var oldMouse = this.mouseArray;

        var parentOffset = $(this.chartContainer).offset();
        this.mouseArray = [e.pageX - parentOffset.left, e.pageY - parentOffset.top];
        var m = this.mouseArray;

        if (scope.$parent.contentObject.getPreferences().canShowTooltip) {
            this.d3chart.selectAll("svg > text").remove();
            if (this.tooltipDelay > 0) {
                if (this.insideGraph(this.mouseArray, false)) {
                    if (!this.showText) {
                        this.$window.setTimeout(() => {
                            this.showText = this.insideGraph(this.mouseArray, false);
                            this.d3chart.selectAll("svg > text").remove();
                            var text = this.displayTooltip(this.mouseArray);
                            text.attr("opacity", 0).transition().delay(100).attr("opacity", 0.75);
                        }, this.tooltipDelay);
                    }
                } else {
                    this.showText = false;
                    clearTimeout();
                }
            } else {
                this.showText = this.insideGraph(this.mouseArray, false);
            }

            if (this.showText) {
                this.displayTooltip(this.mouseArray);
            }
        }

        if (this.canShowAutoLine) {
            this.canDrawLine = false;
        }

        if (this.drawLine) {
            if (!this.insideGraph(this.mouseArray, true)) {
                this.chartContainer.css("cursor", "");
            } else {
                this.chartContainer.css("cursor", "crosshair");
            }
        }

        if (!this.showLine || !this.insideGraph(m, true)) {
            return;
        }

        if (this.canDrawLine && this.drawLine) {
            this.line.attr("x2", m[0] - this.offsetLeft)
                .attr("y2", m[1] - this.offsetTop);
            if (this.dot1) {
                this.dot1.attr("cx", this.line.attr("x1"))
                    .attr("fill", this.dotColor)
                    .attr("cy", this.line.attr("y1"));
            }
            if (this.dot2) {
                this.dot2.attr("cx", this.line.attr("x2"))
                    .attr("fill", this.dotColor)
                    .attr("cy", this.line.attr("y2"));
            }
            return;
        } else if (this.moveLine) {
            var dX = m[0] - oldMouse[0],
                dY = m[1] - oldMouse[1];

            var newX1 = parseInt(this.line.attr("x1")) + dX,
                newY1 = parseInt(this.line.attr("y1")) + dY,
                newX2 = parseInt(this.line.attr("x2")) + dX,
                newY2 = parseInt(this.line.attr("y2")) + dY;

            if (newX1 < this.paddingLeft + this.canvasEdge || newX2 > this.width - this.paddingRight - this.canvasEdge ||
                newX2 < this.paddingLeft + this.canvasEdge || newX1 > this.width - this.paddingRight - this.canvasEdge) {
                newX1 = this.line.attr("x1");
                newX2 = this.line.attr("x2");
            }
            if (newY2 < this.paddingTop + this.canvasEdge || newY1 > this.height - this.paddingBottom - this.canvasEdge ||
                newY1 < this.paddingTop + this.canvasEdge || newY2 > this.height - this.paddingBottom - this.canvasEdge) {
                newY1 = this.line.attr("y1");
                newY2 = this.line.attr("y2");
            }

            this.line.attr("x1", newX1)
                .attr("y1", newY1)
                .attr("x2", newX2)
                .attr("y2", newY2);
            scope.line = {
                x1: this.pixelToCoordX(newX1),
                y1: this.pixelToCoordY(newY1),
                x2: this.pixelToCoordX(newX2),
                y2: this.pixelToCoordY(newY2),
                show: scope.line.show
            };
            if (this.dot1) {
                this.dot1.attr("cx", newX1)
                    .attr("cy", newY1);
            }
            if (this.dot2) {
                this.dot2.attr("cx", newX2)
                    .attr("cy", newY2);
            }
            return;
        }

        var showDot1 = 0,
            showDot2 = 0;

        if (Math.abs(this.coordToPixelX(scope.line.x1) - m[0]) < this.alignOffset && Math.abs(this.coordToPixelY(scope.line.y1) - m[1]) < this.alignOffset) {
            if (!this.oldCanAlign1) {
                this.oldCanAlign1 = true;
                this.d3chart.selectAll("svg > circle").remove();
                showDot1 = 2;
                showDot2 = 1;
                this.chartContainer.css("cursor", "pointer");
                this.chartContainer.off('mousedown');
                this.chartContainer.on("mousedown", () => {
                    this.d3chart.selectAll("svg > line").remove();
                    var tmp = scope.line.x1;
                    scope.line.x1 = scope.line.x2;
                    scope.line.x2 = tmp;
                    tmp = scope.line.y1;
                    scope.line.y1 = scope.line.y2;
                    scope.line.y2 = tmp;
                    this.offsetLeft = m[0] - this.coordToPixelX(scope.line.x2);
                    this.offsetTop = m[1] - this.coordToPixelY(scope.line.y2);
                    this.line = this.d3chart.append("line")
                        .attr("x1", this.coordToPixelX(scope.line.x1))
                        .attr("y1", this.coordToPixelY(scope.line.y1))
                        .attr("x2", this.coordToPixelX(scope.line.x2))
                        .attr("y2", this.coordToPixelY(scope.line.y2))
                        .attr("stroke-dasharray", this.lineStyle)
                        .style("stroke", this.lineColor)
                        .attr("stroke-width", 3)
                        .attr("class", "lobf auto");
                    this.drawLine = true;
                    this.oldCanAlign1 = false;
                    this.chartContainer.off('mousedown');
                    this.chartContainer.on("mousedown", this.secondMouseDown.bind(this, scope));
                });
            }
        } else {
            if (this.oldCanAlign1) {
                this.oldCanAlign1 = false;
                this.d3chart.selectAll("svg > circle").remove();
                this.chartContainer.off('mousedown');
                this.chartContainer.on("mousedown", this.mousedown.bind(this, scope));
                this.chartContainer.css("cursor", "crosshair");
                this.oldCanMove = false;
            }

            if (Math.abs(this.coordToPixelX(scope.line.x2) - m[0]) < this.alignOffset && Math.abs(this.coordToPixelY(scope.line.y2) - m[1]) < this.alignOffset) {
                if (!this.oldCanAlign2) {
                    this.oldCanAlign2 = true;
                    this.d3chart.selectAll("svg > circle").remove();
                    showDot2 = 2;
                    showDot1 = 1;
                    this.chartContainer.css("cursor", "pointer");
                    this.chartContainer.off('mousedown');
                    this.chartContainer.on("mousedown", () => {
                        this.d3chart.selectAll("svg > line").remove();
                        this.offsetLeft = m[0] - this.coordToPixelX(scope.line.x2);
                        this.offsetTop = m[1] - this.coordToPixelY(scope.line.y2);
                        this.drawLine = true;
                        this.oldCanAlign2 = false;
                        this.line = this.d3chart.append("line")
                            .attr("x1", this.coordToPixelX(scope.line.x1))
                            .attr("y1", this.coordToPixelY(scope.line.y1))
                            .attr("x2", m[0] - this.offsetLeft)
                            .attr("y2", m[1] - this.offsetTop)
                            .attr("stroke-dasharray", this.lineStyle)
                            .style("stroke", this.lineColor)
                            .attr("stroke-width", 3)
                            .attr("class", "lobf auto");
                        this.chartContainer.off('mousedown');
                        this.chartContainer.on("mousedown", this.secondMouseDown.bind(this, scope));
                    });
                }
            } else {
                if (this.oldCanAlign2) {
                    this.oldCanAlign2 = false;
                    this.d3chart.selectAll("svg > circle").remove();
                    this.chartContainer.off('mousedown');
                    this.chartContainer.on("mousedown", this.mousedown.bind(this, scope));
                    this.chartContainer.css("cursor", "crosshair");
                    this.oldCanMove = false;
                }
            }
            if (this.canDrawLine && !this.canShowAutoLine) {
                if (showDot1 === 1) {
                    this.dot1 = this.d3chart.append("circle")
                        .attr("cx", this.coordToPixelX(scope.line.x1))
                        .attr("cy", this.coordToPixelY(scope.line.y1))
                        .attr("stroke", this.dotColor)
                        .attr("fill", "white")
                        .attr("stroke-width", 3)
                        .attr("r", 3);
                } else if (showDot1 === 2) {
                    this.dot1 = this.d3chart.append("circle")
                        .attr("cx", this.coordToPixelX(scope.line.x1))
                        .attr("cy", this.coordToPixelY(scope.line.y1))
                        .attr("stroke", this.dotColor)
                        .attr("fill", this.dotColor)
                        .attr("stroke-width", 3)
                        .attr("r", 3);
                }
                if (showDot2 === 1) {
                    this.dot2 = this.d3chart.append("circle")
                        .attr("cx", this.coordToPixelX(scope.line.x2))
                        .attr("cy", this.coordToPixelY(scope.line.y2))
                        .attr("stroke", this.dotColor)
                        .attr("fill", "white")
                        .attr("stroke-width", 3)
                        .attr("r", 3);
                } else if (showDot2 === 2) {
                    this.dot2 = this.d3chart.append("circle")
                        .attr("cx", this.coordToPixelX(scope.line.x2))
                        .attr("cy", this.coordToPixelY(scope.line.y2))
                        .attr("stroke", this.dotColor)
                        .attr("fill", this.dotColor)
                        .attr("stroke-width", 3)
                        .attr("r", 3);
                }
            }
        }
    }

    endMoveLine(scope, e) {
        var parentOffset = $(this.chartContainer).offset();
        var m = [e.pageX - parentOffset.left, e.pageY - parentOffset.top];

        if (!this.insideGraph(m, true)) {
            return;
        }
        this.d3chart.selectAll("svg > circle").remove();
        this.moveLine = false;
        var x1 = this.line.attr("x1"),
            x2 = this.line.attr("x2"),
            y1 = this.line.attr("y1"),
            y2 = this.line.attr("y2");
        scope.line = {
            x1: this.pixelToCoordX(x1),
            y1: this.pixelToCoordY(y1),
            x2: this.pixelToCoordX(x2),
            y2: this.pixelToCoordY(y2),
            show: scope.line.show
        };
        this.oldCanMove = false;
        this.chartContainer.css("cursor", "crosshair");
        this.chartContainer.off('mousedown');
        this.chartContainer.on("mousedown", this.mousedown.bind(this, scope));

        // y-intercept:
        this.lineB = -(this.lineM * this.pixelToCoordX(x1) - this.pixelToCoordY(y1));
    }

    generateAutoLine(scope) {
        console.log("generate AutoLine");

        this.$window.d3.selectAll("svg > line").remove();

        for (var j = 0; j < scope.chartData.length; j++) {
            // calculate the mean of x and y
            var mX = 0,
                mY = 0,
                m0 = 0,
                m1 = 0;
            var series1 = scope.chartData[j].values;
            for (var k in series1) {
                mX += series1[k].x;
                mY += series1[k].y;
            }
            mX /= series1.length;
            mY /= series1.length;

            // calculate the slope
            for (var l in series1) {
                m0 += (series1[l].x - mX) * (series1[l].y - mY);
                m1 += Math.pow((series1[l].x - mX), 2);
            }
            var m = m0 / m1;

            // calculate the y-intercept
            var b = mY - m * mX;

            // first and last coords
            var first = [this.minX, m * this.minX + b];
            var last = [this.maxX, m * this.maxX + b];

            // if this LOBF can not be calculated, skip to the next iteration
            if (isNaN(first[1])) {
                continue;
            }

            // draw the line
            if (first[1] < this.minY) {
                first = [(this.minY - b) / m, this.minY];
            } else if (first[1] > this.maxY) {
                first = [(this.maxY - b) / m, this.maxY];
            }
            if (last[1] < this.minY) {
                last = [(this.minY - b) / m, this.minY];
            } else if (last[1] > this.maxY) {
                last = [(this.maxY - b) / m, this.maxY];
            }
            this.lineM = m;
            this.lineB = b;

            var x1, x2, y1, y2;
            x1 = this.coordToPixelX(first[0]);
            y1 = this.coordToPixelY(first[1]);
            x2 = this.coordToPixelX(last[0]);
            y2 = this.coordToPixelY(last[1]);

            this.d3chart.append('line')
                .attr("x1", x1)
                .attr("y1", y1)
                .attr("x2", x2)
                .attr("y2", y2)
                .attr("stroke-dasharray", this.lineStyle)
                .style("stroke", this.lineColor)
                .attr("stroke-width", 3)
                .attr("class", "lobf auto");
        }
    }

    displayTooltip(m) {
        var x = m[0];
        if (m[0] > this.width - this.paddingRight - 106) {
            x = this.width - this.paddingRight - 106;
        }
        var y = m[1];
        if (m[1] > this.height - this.paddingBottom - 32) {
            y = this.height - this.paddingBottom - 32;
        }

        return this.d3chart.append("text")
            .attr("x", x + 16)
            .attr("y", y + 32)
            .style("opacity", 0.75)
            .text(this.pixelToCoordX(this.mouseArray[0]).toFixed(2) + " | " + this.pixelToCoordY(this.mouseArray[1]).toFixed(2));
    }

    secondMouseDown(scope, e) {
        var parentOffset = $(this.chartContainer).offset();
        var m = [e.pageX - parentOffset.left, e.pageY - parentOffset.top];

        if (!this.insideGraph(m, true)) {
            return;
        }
        this.drawLine = false;

        this.chartContainer.off('mousedown');
        this.chartContainer.on("mousedown", this.mousedown.bind(this, scope));

        var x1 = this.line[0][0].x1.baseVal.value,
            y1 = this.line[0][0].y1.baseVal.value,
            x2 = m[0] - this.offsetLeft,
            y2 = m[1] - this.offsetTop;

        if (x1 > x2) {
            var tmp = x1;
            x1 = x2;
            x2 = tmp;
            tmp = y1;
            y1 = y2;
            y2 = tmp;
        }
        scope.line = {
            x1: this.pixelToCoordX(x1),
            y1: this.pixelToCoordY(y1),
            x2: this.pixelToCoordX(x2),
            y2: this.pixelToCoordY(y2),
            show: scope.line.show
        };

        // calculate function
        // delta X and delta Y
        var dX = this.pixelToCoordX(x2) - this.pixelToCoordX(x1),
            dY = this.pixelToCoordY(y2) - this.pixelToCoordY(y1);
        // slope:
        this.lineM = dY / dX;
        // y-intercept:
        this.lineB = -(this.lineM * this.pixelToCoordX(x1) - this.pixelToCoordY(y1));

        this.offsetLeft = 0;
        this.offsetTop = 0;
    }

    displayLine(show) {
        show = show || this.isPDF;
        this.showLine = show;
        scope.line.show = show;
        this.lineButton.attr("src", show ? this.icons.hide : (this.canShowAutoLine ? this.icons.show : this.icons.draw));

        if (show) {
            this.d3chart.selectAll("svg > line").style("visibility", "visible");
            if (this.canDrawLine) {
                this.chartContainer.css("cursor", "crosshair");
            }
        } else {
            this.d3chart.selectAll("svg > line").style("visibility", "hidden");
            this.chartContainer.css("cursor", "");
        }
    }

    displayButton(scope) {
        this.lineButton = $(this.d3chart[0]).next();
        if (this.isPDF || this.lineButton.attr("alt") == 'PDF') {
            console.log("----- detected PDF ------");
            this.isPDF = true;
            if (this.canShowAutoLine || this.canDrawLine) {
                this.showLine = true;
                this.displayLine(true);
            }
            return;
        }
        if (this.canShowAutoLine || this.canDrawLine) {
            this.lineButton.off("click");
            this.displayLine(this.showLine);
            this.lineButton.on("click", function() {
                if (this.moveLine || this.drawLine) {
                    return;
                }
                this.displayLine(!this.showLine);
            }.bind(this));
            this.lineButton.css("visibility", "visible");
        } else {
            this.lineButton.css("visibility", "hidden");
        }
    }

    init(scope, element) {
        if (this.drawLine || this.moveLine) {
            return;
        }

        if (typeof(scope.chartData) != 'undefined' && scope.chartData.length >= 1) {
            this.canShowAutoLine = scope.$parent.contentObject.getPreferences().canShowAutoLine;
            this.canDrawLine = scope.$parent.contentObject.getPreferences().canDrawLine && !this.canShowAutoLine;

            this.moveLine = false;
            this.drawLine = false;
            this.oldCanAlign1 = false;
            this.oldCanAlign2 = false;
            this.oldCanMove = false;

            // get constraints
            this.minX = undefined;
            this.maxX = undefined;
            this.minY = undefined;
            this.maxY = undefined;
            for (var i = 0; i < scope.chartData.length; i++) {
                var series1 = scope.chartData[i].values;
                for (var s in series1) {
                    if (typeof(this.minX) == 'undefined' || series1[s].x < this.minX) {
                        this.minX = series1[s].x;
                    }
                    if (typeof(this.maxX) == 'undefined' || series1[s].x > this.maxX) {
                        this.maxX = series1[s].x;
                    }
                    if (typeof(this.minY) == 'undefined' || series1[s].y < this.minY) {
                        this.minY = series1[s].y;
                    }
                    if (typeof(this.maxY) == 'undefined' || series1[s].y > this.maxY) {
                        this.maxY = series1[s].y;
                    }
                }
            }

            this.$window.nv.addGraph(() => {
                console.log('nvd3_scatter_init');

                var chart = this.$window.nv.models.scatterChart()
                    .showDistX(false)    //showDist, when true, will display those little distribution lines on the axis.
                    .showDistY(false)
                    .color(["#1f77b4", "#B10DC9", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"]);

                chart.yAxis.axisLabel(scope.yLabel).axisLabelDistance(0);

                for (var property in chart.legend.dispatch) {
                    chart.legend.dispatch[property] = function() {};
                }
                
                this.d3chart = this.$window.d3.select(this.element.find('svg')[0]).datum(scope.chartData);
                this.d3chart.call(chart);
                this.$window.nv.utils.windowResize(chart.update);

                this.width = $(this.d3chart[0]).width();
                this.height = $(this.d3chart[0]).height();

                this.pixelToCoordX = this.$window.d3.scale.linear()
                    .domain([this.paddingLeft, this.width - this.paddingRight])
                    .range([this.minX, this.maxX]);
                this.pixelToCoordY = this.$window.d3.scale.linear()
                    .domain([this.height - this.paddingBottom, this.paddingTop])
                    .range([this.minY, this.maxY]);

                this.coordToPixelX = this.$window.d3.scale.linear()
                    .domain([this.minX, this.maxX])
                    .range([this.paddingLeft, this.width - this.paddingRight]);
                this.coordToPixelY = this.$window.d3.scale.linear()
                    .domain([this.minY, this.maxY])
                    .range([this.height - this.paddingBottom, this.paddingTop]);

                this.chartContainer = $($(this.d3chart[0]).parent()[0]);

                this.chartContainer.off("mousedown");
                this.chartContainer.on("mousemove", this.mousemove.bind(this));

                if (scope.line.x1 && !this.canShowAutoLine) {
                    this.$window.d3.selectAll("svg > line").remove();
                    this.line = this.d3chart.append('line')
                        .attr("x1", this.coordToPixelX(scope.line.x1))
                        .attr("y1", this.coordToPixelY(scope.line.y1))
                        .attr("x2", this.coordToPixelX(scope.line.x2))
                        .attr("y2", this.coordToPixelY(scope.line.y2))
                        .style("stroke", this.lineColor)
                        .attr("stroke-dasharray", this.lineStyle)
                        .attr("class", "lobf manual");
                }
                if (this.canShowAutoLine && this.coordToPixelX && this.pixelToCoordX) {
                    this.generateAutoLine(scope);
                } else if (this.canDrawLine) {
                    this.chartContainer.on('mousedown', this.mousedown.bind(this, scope));
                } else {
                    this.chartContainer.off("mousemove");
                }

                this.$window.nv.utils.windowResize(chart.update);

                this.showLine = (typeof(scope.line.show) === 'undefined') ? false : scope.line.show;
                this.displayButton(scope);
            });
        }
    }
}

export default IbD3ScatterChart;